import React, { useEffect } from 'react';
import { useUserEntitlements } from 'hooks/useUserEntitlements';
import Route404 from 'App/Router/Route404';
import { logException } from 'App/logging';
import type {
  ChatbotMessageSentSource,
  UserMessage,
} from 'Assistant/Chatbot/types';
import { isChatbotMessageSentSource } from 'Assistant/Chatbot/utils/isChatbotMessageSentSource';
import { useHistory, useLocation } from 'react-router';
import uuid from 'uuid';
import { isMSTeams } from 'utils/MSTeams';
import { MappedMessageType } from 'Assistant/__generated__/client.schemas';
import LoadingIndicator from 'Shared/LoadingIndicator';
import { Chatbot } from '../../components/Chatbot';
import { PageContainer } from './styles';

type VoiceFeedbackNavState = {
  requestVoiceFeedback?: boolean;
};

type InitialMessageTracking = {
  source?: ChatbotMessageSentSource;
};

type ChatbotPageState =
  | (VoiceFeedbackNavState & InitialMessageTracking & UserMessage)
  | undefined;

const mapUserMessageFromState = (
  state: ChatbotPageState,
  urlParams: URLSearchParams,
): UserMessage | undefined => {
  if (isMSTeams()) {
    const content = urlParams.get('content');

    return content
      ? {
          content,
          topic: urlParams.get('topic'),
          starterId: urlParams.get('starterId'),
          id: uuid.v4(),
          createdAt: new Date().toISOString(),
          type: MappedMessageType.text,
        }
      : undefined;
  }

  if (!state) {
    return undefined;
  }

  const { requestVoiceFeedback, source, ...userMessage } = state;

  // Route state may carry only non-message fields (e.g. requestVoiceFeedback
  // when returning from VoicePage). After stripping those, an empty object is
  // not a real message — return undefined so it isn't treated as an
  // initialMessage (which would be truthy and alter transparency/follow-up).
  return Object.keys(userMessage).length > 0 ? userMessage : undefined;
};

// The originating UI sets `source` explicitly (via route state, or a URL param
// in MS Teams). An initial message with a missing/invalid source is a bug, so
// we track it as 'unknown' and log it rather than guessing. With no initial
// message there's nothing to attribute, so submitMessage's default applies.
const mapInitialMessageSource = (
  state: ChatbotPageState,
  urlParams: URLSearchParams,
  hasInitialMessage: boolean,
): ChatbotMessageSentSource | undefined => {
  const rawSource = urlParams.get('source') ?? state?.source;

  if (isChatbotMessageSentSource(rawSource)) {
    return rawSource;
  }

  if (!hasInitialMessage) {
    return undefined;
  }

  logException(
    new Error('Nova initial message received with an unknown source'),
    {
      tags: {
        product: 'nova',
        feature: 'nova-chat',
        action: 'map-initial-message-source',
      },
      extra: { rawSource },
    },
  );

  return 'unknown';
};

export const ChatbotPage = () => {
  const { entitlements, loading: entitlementsLoading } = useUserEntitlements();
  const { state, search } = useLocation<ChatbotPageState>();
  const history = useHistory();

  const urlParams = new URLSearchParams(search);
  const userMessage = mapUserMessageFromState(state, urlParams);
  const initialMessageSource = mapInitialMessageSource(
    state,
    urlParams,
    Boolean(userMessage),
  );
  const np = urlParams.get('np') ?? undefined;
  const sig = urlParams.get('sig') ?? undefined;
  const [showVoiceFeedback, setShowVoiceFeedback] = React.useState(false);

  useEffect(() => {
    if (state?.requestVoiceFeedback) {
      setShowVoiceFeedback(true);
      history.replace(window.location.pathname);
    }
  }, [state, history]);

  if (entitlementsLoading) {
    return <LoadingIndicator />;
  }

  if (!entitlements.nova) {
    return <Route404 />;
  }

  return (
    <PageContainer>
      <Chatbot
        initialMessage={userMessage}
        initialMessageSource={initialMessageSource}
        showVoiceFeedback={showVoiceFeedback}
        np={np}
        sig={sig}
      />
    </PageContainer>
  );
};
